

import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Pencil,
  X,
  Save,
  User,
  Clock,
} from "lucide-react";
import Sidebar from "../../components/ui/Sidebar";
import Header from "../../components/ui/Header";
import axiosInstance from "../../utils/axiosInstance";

const PAGE_SIZE = 10;

// ✅ adjust if you have real cart statuses in DB
const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "CONVERTED", label: "Converted" },
  { value: "ABANDONED", label: "Abandoned" },
];

const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "all", label: "All" },
  { value: "custom", label: "Custom Range" },
];

const toYMD = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ✅ returns {fromDate, toDate} (YYYY-MM-DD)
const computeRangeFromPreset = (preset) => {
  const now = new Date();
  const today = toYMD(now);

  if (preset === "today") return { fromDate: today, toDate: today };

  if (preset === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const yd = toYMD(y);
    return { fromDate: yd, toDate: yd };
  }

  if (preset === "last7") {
    const start = new Date(now);
    start.setDate(start.getDate() - 6); // includes today
    return { fromDate: toYMD(start), toDate: today };
  }

  // all
  return { fromDate: "", toDate: "" };
};

const CartsPage = () => {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [meta, setMeta] = useState({ total: 0, perPage: PAGE_SIZE });

  const [search, setSearch] = useState("");

  // ✅ default today
  const [datePreset, setDatePreset] = useState("today");
  const [fromDate, setFromDate] = useState(() => computeRangeFromPreset("today").fromDate);
  const [toDate, setToDate] = useState(() => computeRangeFromPreset("today").toDate);

  // ✅ status filter
  const [status, setStatus] = useState("");

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ✅ modal states
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);
  const [editRemark, setEditRemark] = useState("");

  const token = useMemo(() => localStorage.getItem("token"), []);
  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const queryParams = useMemo(() => {
    // for preset (not custom), automatically compute (today/yesterday/last7/all)
    const range =
      datePreset === "custom" ? { fromDate, toDate } : computeRangeFromPreset(datePreset);

    const params = {
      page,
      limit: PAGE_SIZE,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(status ? { status } : {}),
      ...(range.fromDate ? { fromDate: range.fromDate } : {}),
      ...(range.toDate ? { toDate: range.toDate } : {}),
    };

    return params;
  }, [page, search, status, datePreset, fromDate, toDate]);

  const fetchCarts = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(`${API_BASE}/api/carts`, {
        params: queryParams,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCarts(res.data?.data || []);
      setTotalPages(res.data?.meta?.totalPages || 1);
      setMeta({
        total: res.data?.meta?.total || 0,
        perPage: res.data?.meta?.perPage || PAGE_SIZE,
      });
    } catch (err) {
      console.error("FETCH CARTS ERROR:", err);
      setCarts([]);
      setTotalPages(1);
      setMeta({ total: 0, perPage: PAGE_SIZE });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleApplyFilters = () => {
    // ✅ validate custom range
    if (datePreset === "custom") {
      if (!fromDate || !toDate) return alert("Please select both From and To date.");
      if (new Date(fromDate) > new Date(toDate)) return alert("From date cannot be after To date.");
    }
    setPage(1);
    fetchCarts();
  };

  const handleReset = () => {
    setSearch("");
    setStatus("");
    setDatePreset("today");

    const r = computeRangeFromPreset("today");
    setFromDate(r.fromDate);
    setToDate(r.toDate);

    setPage(1);
    fetchCarts();
  };

  const getSerialNumber = (index) => (page - 1) * PAGE_SIZE + index + 1;

  const handleMenuToggle = () => setSidebarOpen((v) => !v);
  const handleSidebarClose = () => setSidebarOpen(false);

  // ✅ open modal
  const openEditModal = (cart) => {
    setSelectedCart(cart);
    setEditRemark(cart.adminRemark || "");
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setIsEditOpen(false);
    setSelectedCart(null);
    setEditRemark("");
  };

  // ✅ save remark
  const saveCartRemark = async () => {
    if (!selectedCart?.id) return;

    try {
      setSaving(true);
      const url = `${API_BASE}/api/carts/admin/${selectedCart.id}/remark`;
      const payload = { adminRemark: editRemark };

      const res = await axiosInstance.patch(url, payload);

      const updatedCart = res.data?.data;

      setCarts((prev) =>
        prev.map((c) =>
          c.id === selectedCart.id
            ? {
                ...c,
                adminRemark: updatedCart?.adminRemark ?? editRemark,
                remarkUpdatedAt: updatedCart?.remarkUpdatedAt ?? c.remarkUpdatedAt,
                remarkUpdatedBy: updatedCart?.remarkUpdatedBy ?? c.remarkUpdatedBy,
              }
            : c
        )
      );

      closeEditModal();
    } catch (err) {
      console.error("UPDATE CART REMARK ERROR:", err);
      alert("Failed to update remark.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const showFromToInputs = datePreset === "custom";

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
   <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-4">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-800">Carts Management</h1>
            <p className="text-sm text-gray-500 mt-1">Default shows Today created carts only</p>
          </div>

          {/* FILTER BAR */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter size={18} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters</span>
              </div>
              <button
                onClick={handleReset}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset Filters
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Patient Search</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Name or phone number"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Preset */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Date</label>
                <div className="relative">
                  <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={datePreset}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDatePreset(v);
                      setPage(1);
                      if (v !== "custom") {
                        const r = computeRangeFromPreset(v);
                        setFromDate(r.fromDate);
                        setToDate(r.toDate);
                      }
                    }}
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  >
                    {DATE_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* From */}
              <div className={`${showFromToInputs ? "" : "opacity-60 pointer-events-none"}`}>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">From</label>
                <div className="relative">
                  <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
                  />
                </div>
              </div>

              {/* To */}
              <div className={`${showFromToInputs ? "" : "opacity-60 pointer-events-none"}`}>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">To</label>
                <div className="relative">
                  <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
                  />
                </div>
              </div>

              {/* Apply */}
              <div className="md:col-span-5 flex justify-end">
                <button
                  onClick={handleApplyFilters}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Search size={14} />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="p-2 text-left font-medium text-gray-600 uppercase tracking-wider w-10">#</th>
                    <th className="p-2 text-left font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">
                      Patient Details
                    </th>
                    <th className="p-2 text-left font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                      Items
                    </th>
                    <th className="p-2 text-left font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">
                      Admin Remark
                    </th>
                    <th className="p-2 text-left font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">
                      Remark Updated
                    </th>
                    <th className="p-2 text-left font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                      Created
                    </th>
                    <th className="p-2 text-left font-medium text-gray-600 uppercase tracking-wider w-20">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="p-6 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                          <p className="text-xs text-gray-500">Loading carts...</p>
                        </div>
                      </td>
                    </tr>
                  ) : carts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-6 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-400">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                            <Search size={16} />
                          </div>
                          <p className="text-xs font-medium">No carts found</p>
                          <p className="text-[10px] mt-0.5">Try adjusting your filters</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    carts.map((cart, index) => (
                      <tr key={cart.id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-2 text-gray-500 text-center font-mono">{getSerialNumber(index)}</td>

                        <td className="p-2">
                          <div className="font-medium text-gray-900">{cart.patient?.fullName || "—"}</div>
                          {cart.patient?.contactNo && (
                            <div className="text-[11px] text-gray-500 mt-0.5">📞 {cart.patient.contactNo}</div>
                          )}
                          {cart.patient?.age && (
                            <div className="text-[11px] text-gray-500">👤 {cart.patient.age} yrs</div>
                          )}
                        </td>

                        <td className="p-2">
                          <div className="space-y-1">
                            {cart.cartItems?.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between">
                                <span className="text-gray-700 truncate max-w-[100px]">
                                  {item.test?.name || item.package?.name || "Unknown"}
                                </span>
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[9px] font-medium ml-1 ${
                                    item.test
                                      ? "bg-blue-50 text-blue-700 border border-blue-100"
                                      : "bg-purple-50 text-purple-700 border border-purple-100"
                                  }`}
                                >
                                  {item.test ? "Test" : "Package"}
                                </span>
                              </div>
                            ))}
                            {cart.cartItems?.length > 0 && (
                              <div className="text-[10px] text-gray-400 mt-1">{cart.cartItems.length} item(s)</div>
                            )}
                          </div>
                        </td>

                        <td className="p-2">
                          {cart.adminRemark ? (
                            <div className="text-xs text-gray-700 line-clamp-3">{cart.adminRemark}</div>
                          ) : (
                            <div className="text-[11px] text-gray-400 italic">No remark</div>
                          )}
                        </td>

                        <td className="p-2">
                          {cart.remarkUpdatedAt ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                <Clock size={10} className="text-gray-400" />
                                <span className="font-medium">{formatDate(cart.remarkUpdatedAt)}</span>
                                <span className="text-gray-400">at</span>
                                <span>{formatTime(cart.remarkUpdatedAt)}</span>
                              </div>
                              {cart.remarkUpdatedBy && (
                                <div className="flex items-center gap-1 text-[10px]">
                                  <User size={10} className="text-gray-400" />
                                  <span className="text-gray-700">{cart.remarkUpdatedBy.name}</span>
                                  <span className="px-1 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px]">
                                    {cart.remarkUpdatedBy.role}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-400 italic">Not updated</div>
                          )}
                        </td>

                        <td className="p-2">
                          <div className="text-[11px]">
                            <div className="text-gray-600 font-medium">{formatDate(cart.createdAt)}</div>
                            <div className="text-gray-400">{formatTime(cart.createdAt)}</div>
                          </div>
                        </td>

                        <td className="p-2">
                          <button
                            onClick={() => openEditModal(cart)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                          >
                            <Pencil size={12} />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer + Pagination */}
            {meta.total > 0 && (
              <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between text-[11px] text-gray-500">
                  <div>
                    Showing{" "}
                    <span className="font-medium">{(page - 1) * meta.perPage + 1}</span> to{" "}
                    <span className="font-medium">
                      {(page - 1) * meta.perPage + carts.length}
                    </span>{" "}
                    of <span className="font-medium">{meta.total}</span> results
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      disabled={page === 1 || loading}
                      onClick={() => setPage((p) => p - 1)}
                      className="flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-[11px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={12} />
                      Prev
                    </button>

                    <div className="flex items-center gap-1 px-2">
                      <span className="text-gray-700 font-medium">{page}</span>
                      <span className="text-gray-400">of</span>
                      <span className="text-gray-700 font-medium">{totalPages}</span>
                    </div>

                    <button
                      disabled={page === totalPages || loading}
                      onClick={() => setPage((p) => p + 1)}
                      className="flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-[11px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                      <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* EDIT MODAL */}
      {isEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-800">Update Cart Remark</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Cart ID: {selectedCart?.id} • Patient: {selectedCart?.patient?.fullName || "—"}
                </div>
              </div>

              <button
                onClick={closeEditModal}
                disabled={saving}
                className="p-2 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {selectedCart?.remarkUpdatedAt && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <div className="text-xs font-medium text-blue-800 mb-1">Last Updated</div>
                  <div className="text-[11px] text-blue-700">
                    <div className="flex items-center gap-1">
                      <Clock size={10} />
                      {formatDate(selectedCart.remarkUpdatedAt)} at {formatTime(selectedCart.remarkUpdatedAt)}
                    </div>
                    {selectedCart?.remarkUpdatedBy && (
                      <div className="flex items-center gap-1 mt-1">
                        <User size={10} />
                        {selectedCart.remarkUpdatedBy.name} ({selectedCart.remarkUpdatedBy.role})
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Admin Remark</label>
                <textarea
                  value={editRemark}
                  onChange={(e) => setEditRemark(e.target.value)}
                  rows={4}
                  placeholder="Add admin remark..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
                  disabled={saving}
                />
                <div className="text-[11px] text-gray-400 mt-1">
                  This remark will be stored on cart (admin visible).
                </div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={closeEditModal}
                disabled={saving}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white transition disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                onClick={saveCartRemark}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartsPage;
